import { IS_AUTH0_SOCIAL_LOGIN_APPLE_ENABLED } from '@/components/constants/flags';
import { useEmailLoginSubmit } from '@/components/modal/sign-in-modal/hooks/useEmailLoginSubmit';
import { iframeSafeAuthRoute } from '@/components/modal/sign-in-modal/utils';
import Loader from '@/components/ui/loader';
import { signInClicked } from '@/lib/analytics/scribe/auth';
import { cn } from '@/lib/cn';
import { useDesktopAuthSafe } from '@/tauri/auth0/desktop-auth-context';
import { isTauri, isUsingReactKinde } from '@/tauri/utils';
import { appendAmplitudeCrossDomainParams } from '@/utils/amplitudeCrossDomain';
import { appendAttributionParamsToRedirectUrl } from '@/utils/attributionParams';
import { isAuth0SocialLoginGoogleAllowed } from '@/utils/auth/isAuth0SocialLoginGoogleAllowed';
import { prefixWithLocale } from '@/utils/localeRedirect';
import { logger } from '@/utils/logger/logger';
import {
  AUTH0_APPLE_CONNECTION,
  AUTH0_GOOGLE_CONNECTION,
  AUTH0_LOGIN_PATH,
} from '@heidi/auth/contracts';
import { Button } from '@heidi/design-system/button';
import { Input } from '@heidi/design-system/input';
import { Separator } from '@heidi/design-system/separator';
import { TypographyP2 } from '@heidi/design-system/typography';
import { setV2Eligible } from '@heidi/growth';
import { toAuth0UiLocales } from '@heidi/utils/i18n';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { Mail } from 'lucide-react';
import Image from 'next/image';
import { useState, type ReactNode } from 'react';
import { Controller, type Control } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { signUpFormMessages } from './SignUpForm.messages';
import { getSocialProviders } from './SignUpForm.social-providers';

type ProviderType = 'email' | 'google' | 'apple';

interface SocialLoginButtonProps {
  provider: 'google' | 'apple';
  icon: string;
  label: ReactNode;
  logoAlt: string;
  isLoading: boolean;
  disabled: boolean;
  onClick: () => void;
}

function SocialLoginButton({
  provider,
  icon,
  label,
  logoAlt,
  isLoading,
  disabled,
  onClick,
}: SocialLoginButtonProps) {
  return (
    <Button
      variant="secondary"
      size="lg"
      disabled={disabled}
      onClick={onClick}
      disableAutomaticSvgSizing
    >
      {isLoading ? (
        <Loader className="size-4.5" />
      ) : (
        <Image
          src={icon}
          alt={logoAlt}
          className={cn('size-4.5', provider === 'apple' && 'dark-invert')}
        />
      )}
      {label}
    </Button>
  );
}

interface SignUpFormProps {
  control: Control<{ email: string }>;
  email: string;
  isEmailInvalid: boolean;
  emailConnectionId: string;
  postLoginRedirectUri?: string;
  isFromMobileWebview?: boolean;
}

export function SignUpForm({
  control,
  email,
  isEmailInvalid,
  emailConnectionId,
  postLoginRedirectUri,
  isFromMobileWebview,
}: SignUpFormProps) {
  const intl = useIntl();
  const { locale } = intl;
  const { login, register: kindeRegister } = useKindeAuth();
  const { login: desktopAuthLogin } = useDesktopAuthSafe();
  const shouldUseDesktopAuth = isTauri();

  const [clicked, setClicked] = useState<ProviderType>();

  const getRedirectUrl = (defaultPath: string) => {
    const basePath = postLoginRedirectUri ?? defaultPath;
    return prefixWithLocale(locale, basePath);
  };

  const postSignupRedirectUrl = appendAmplitudeCrossDomainParams(
    appendAttributionParamsToRedirectUrl(getRedirectUrl('/scribe/new-user'))
  );

  const { submitViaDetermineLogin, isDetermineLoginEnabled, isDetermineLoginFetching } =
    useEmailLoginSubmit({
      email,
      postLoginRedirectUri: postSignupRedirectUrl,
      emailConnectionId,
    });

  const emailRegisterQuery = {
    login_hint: email,
    post_login_redirect_url: postSignupRedirectUrl,
    connection_id: emailConnectionId,
  };

  const handleEmailClick = () => {
    setClicked('email');
    setV2Eligible();
    signInClicked('email');
  };

  const handleEmailSubmit = () => {
    handleEmailClick();
    if (isDetermineLoginEnabled || isFromMobileWebview) {
      void submitViaDetermineLogin();
      return;
    }
    if (isUsingReactKinde()) {
      const kindeParams = {
        authUrlParams: { ...emailRegisterQuery, lang: locale },
        app_state: { redirectTo: postSignupRedirectUrl },
      };
      void kindeRegister(kindeParams);
    } else {
      const url = new URL('/api/auth/register', window.location.origin);
      url.searchParams.set('login_hint', email);
      url.searchParams.set('post_login_redirect_url', postSignupRedirectUrl);
      url.searchParams.set('connection_id', emailConnectionId);
      iframeSafeAuthRoute(url.toString());
    }
  };

  const handleSocialLogin = (provider: 'google' | 'apple', kindeConnectionId: string) => {
    setClicked(provider);
    setV2Eligible();
    signInClicked(provider);

    const isAuth0Social =
      provider === 'google'
        ? isAuth0SocialLoginGoogleAllowed()
        : IS_AUTH0_SOCIAL_LOGIN_APPLE_ENABLED();

    if (isAuth0Social) {
      const auth0Connection =
        provider === 'google' ? AUTH0_GOOGLE_CONNECTION : AUTH0_APPLE_CONNECTION;

      if (shouldUseDesktopAuth) {
        void desktopAuthLogin({
          connection: auth0Connection,
          uiLocales: toAuth0UiLocales(locale),
        }).catch((error) => {
          logger.error(`[Auth0] Desktop ${provider} login failed`, error);
        });
        return;
      }

      const params = new URLSearchParams();
      params.set('returnTo', postSignupRedirectUrl);
      params.set('connection', auth0Connection);
      if (locale) params.set('ui_locales', toAuth0UiLocales(locale));
      iframeSafeAuthRoute(`${AUTH0_LOGIN_PATH}?${params.toString()}`);
      return;
    }

    if (isUsingReactKinde()) {
      void login({
        authUrlParams: {
          post_login_redirect_url: postSignupRedirectUrl,
          connection_id: kindeConnectionId,
          lang: locale,
        },
      });
    } else {
      const url = new URL('/api/auth/login', window.location.origin);
      url.searchParams.set('post_login_redirect_url', postSignupRedirectUrl);
      url.searchParams.set('connection_id', kindeConnectionId);
      iframeSafeAuthRoute(url.toString());
    }
  };

  const isButtonDisabled = clicked !== undefined;
  const isEmailButtonDisabled =
    !email || isEmailInvalid || isButtonDisabled || isDetermineLoginFetching;

  const continueButtonContent =
    clicked === 'email' ? (
      <Loader className="size-5 text-text-primary-invert" />
    ) : (
      <FormattedMessage {...signUpFormMessages.continue} />
    );

  return (
    <div className="flex w-full max-w-sm flex-col gap-4 rounded-2xl border border-border bg-surface p-6">
      {/* Email Input */}
      <div className="flex flex-col gap-1">
        <TypographyP2 className="font-medium">
          <FormattedMessage {...signUpFormMessages.workEmailLabel} />
        </TypographyP2>
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              id="email"
              type="email"
              placeholder={intl.formatMessage(signUpFormMessages.workEmailPlaceholder)}
              autoComplete="email"
              accessoryStart={<Mail size={16} className="text-text-disabled" />}
              data-testid="signup-email-input"
            />
          )}
        />
      </div>

      {/* Buttons */}
      <div className="flex flex-col gap-2">
        {/* Email Continue Button */}
        <Button
          size="lg"
          disabled={isEmailButtonDisabled}
          onClick={handleEmailSubmit}
          data-testid="signup-continue-button"
        >
          {continueButtonContent}
        </Button>

        {/* Divider */}
        <div className="flex w-full items-center gap-6">
          <Separator className="flex-1" />
          <TypographyP2 className="text-text-tertiary">
            <FormattedMessage {...signUpFormMessages.orDivider} />
          </TypographyP2>
          <Separator className="flex-1" />
        </div>

        {/* Social Login Buttons */}
        {getSocialProviders().map(
          ({ id, connectionId, icon, labelMessage, logoAltMessage }) => (
            <SocialLoginButton
              key={id}
              provider={id}
              icon={icon}
              label={<FormattedMessage {...labelMessage} />}
              logoAlt={intl.formatMessage(logoAltMessage)}
              isLoading={clicked === id}
              disabled={isButtonDisabled}
              onClick={() => handleSocialLogin(id, connectionId)}
            />
          )
        )}
      </div>
    </div>
  );
}
